import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { listResearchReports, deleteResearchReport } from "@/api/research";

function prefSummary(pref: string): string {
  if (!pref) return "";
  const m: Record<string, string> = {};
  pref.split(/[，,、]/).forEach(p => {
    const [k, ...v] = p.split(/[:：]/);
    if (k && v.length) m[k.trim()] = v.join(":").trim();
  });
  const parts: string[] = [];
  if (m["purpose"]) parts.push(m["purpose"]);
  if (m["direction"]) parts.push(m["direction"]);
  if (m["horizon"]) parts.push(m["horizon"].replace(/[（(].*[）)]/, ""));
  if (m["focus"]) parts.push(m["focus"]);
  if (m["risk"]) parts.push(m["risk"]);
  if (m["valuation"]) parts.push(m["valuation"].replace(/[（(].*[）)]/, ""));
  return parts.length ? parts.join(" · ") : "";
}

export default function ResearchLibrary() {
  const navigate = useNavigate();
  const [reports, setReports] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("全部");
  const [deleting, setDeleting] = useState<number | null>(null);

  const load = () => {
    setLoading(true);
    listResearchReports()
      .then((d) => setReports(d.list || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  // ── Poll every 3s while any report is still "生成中" ──
  const pendingRef = useRef(false);
  useEffect(() => {
    pendingRef.current = reports.some((r: any) => r.status === "生成中");
  }, [reports]);
  useEffect(() => {
    const timer = setInterval(() => {
      if (!pendingRef.current) return;
      listResearchReports()
        .then((d) => setReports(d.list || []))
        .catch(() => {});
    }, 3_000);
    return () => clearInterval(timer);
  }, []);

  const handleDelete = async (id: number) => {
    if (!confirm("确定要删除此研报吗？")) return;
    setDeleting(id);
    try { await deleteResearchReport(id); load(); }
    catch { alert("删除失败"); }
    finally { setDeleting(null); }
  };

  const filtered = filter === "全部" ? reports : reports.filter((r: any) => r.status === filter);

  return (
    <div className="flex-1 overflow-y-auto px-4 py-4">
      <div className="flex items-center gap-3 mb-4">
        <button onClick={() => navigate(-1)} className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center cursor-pointer"><i className="ri-arrow-left-s-line text-foreground-600 text-lg" /></button>
        <h1 className="text-lg font-bold text-foreground-950">我的研报库</h1>
      </div>
      <div className="flex gap-2 mb-3 overflow-x-auto scrollbar-hide">
        {["全部", "已完成", "生成中", "失败"].map((f) => (
          <button key={f} onClick={() => setFilter(f)}
            className={`text-[11px] px-3 py-1 rounded-full font-medium cursor-pointer whitespace-nowrap ${filter === f ? "bg-primary-500 text-white" : "bg-background-100 text-foreground-500"}`}>{f}</button>
        ))}
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-12"><div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12"><p className="text-sm text-foreground-400">暂无研报</p></div>
      ) : (
        <div className="space-y-2">
          {filtered.map((r: any) => (
            <div key={r.id} className="bg-white rounded-xl p-4 border border-background-200/60">
              <div className="flex items-center justify-between cursor-pointer" onClick={() => navigate(`/research/report/${r.id}`)}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-foreground-950">{r.stockName}</span>
                    <span className="text-[10px] text-foreground-400">{r.stockCode}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold" style={{ color: "#E11D48", background: "rgba(225,29,72,0.1)" }}>{r.rating || "--"}</span>
                    {r.status === "生成中" && <span className="ml-auto w-3.5 h-3.5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin shrink-0" />}
                  </div>
                  <div className="text-[10px] text-foreground-400 mt-0.5">
                    {r.createdAt ? new Date(r.createdAt * 1000).toLocaleDateString() : ""}
                  </div>
                  {r.preference && prefSummary(r.preference) && (
                    <div className="text-[10px] text-foreground-500 mt-1 leading-relaxed">
                      {prefSummary(r.preference)}
                    </div>
                  )}
                </div>
                <i className="ri-arrow-right-s-line text-foreground-300" />
              </div>
              <div className="flex gap-2 mt-2 pt-2 border-t border-background-100">
                <button onClick={() => navigate(`/research/report/${r.id}`)}
                  className="text-[10px] px-3 py-1 rounded-full bg-primary-50 text-primary-600 cursor-pointer">查看</button>
                <button onClick={() => handleDelete(r.id)} disabled={deleting === r.id}
                  className="text-[10px] px-3 py-1 rounded-full bg-red-50 text-red-500 cursor-pointer disabled:opacity-50">{deleting === r.id ? "删除中..." : "删除"}</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
